/**
 * THE FREEZE RAY — fire along your heading, and whoever it reaches stops being able to do
 * anything for five seconds.
 *
 * The player report, in full: "another power-up could be a freeze ray which shoots like the
 * missile and freezes a circle if it hits one for 5 seconds". So the *motion* is the rocket's —
 * launched along the heading in force, bounded by a range and by a clock — and everything new is
 * on the other side of the contact: this is the first thing in the game that leaves a **timed
 * status on a player** rather than reshaping them on the tick it lands.
 *
 * **A FROZEN PLAYER IS STILL EDIBLE, and that is the whole difference between this and a duel.**
 * A duellist is frozen *and* untouchable, because a duel is a fight between two people that a
 * third must not be able to help themselves to. This freeze is the opposite mechanic: it takes
 * somebody's agency away and leaves their body in the room, which is the entire reason to fire
 * it. What a frozen player keeps and loses is spelled out on `PlayerState.frozenUntilTick` and
 * enforced per phase of `step`, exactly as a duellist's freeze is.
 *
 * It is therefore the strongest thing in the registry against a player who is *worth* eating and
 * the weakest against one who is not, which is what the shorter range below is paying for: a
 * rocket costs its target a cluster it can rebuild, and five seconds of standing still in an .io
 * game is usually the run.
 */

import { PROJECTILE_MUZZLE_CLEARANCE, TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_FREEZE = 'freeze';

/**
 * How long a hit freezes somebody, in seconds. **Five**, which is the number the report asked
 * for, and it is a whole mechanic rather than a duration: at BASE_SPEED it is most of the way
 * across a camera, so a freeze that lands is somebody else's five seconds to close the distance.
 */
export const FREEZE_DURATION_SECONDS = 5;

/** The same number the simulation actually counts in. `PlayerState.frozenUntilTick` is a tick. */
export const FREEZE_DURATION_TICKS = FREEZE_DURATION_SECONDS * TICK_RATE_HZ;

/**
 * World units per second — the rocket's, deliberately, because the report's own word for this
 * is "shoots like the missile". A ray a starting player could outrun in a straight line would be
 * a ray nobody is ever hit by, and dodging one by *turning* is the skill both of them are tuned
 * around.
 */
export const FREEZE_SPEED = 900;

/**
 * How far it may travel, in world units — shorter than the rocket's, and that is the price of
 * what it does.
 *
 * A rocket breaks a cluster the target can put back together; this takes away everything they
 * can do for FREEZE_DURATION_SECONDS and leaves them edible while it runs. The two cannot cost
 * the same, and the honest place to charge for it is the distance you have to be inside — a
 * freeze is fired from somewhere you could plausibly have been eaten from yourself.
 */
export const FREEZE_RANGE = 1_000;

/** Thinner than a rocket: it is a shard of ice rather than a warhead. */
export const FREEZE_RADIUS = 14;

/**
 * The second bound, in ticks, derived from the first exactly as the rocket's is, so the two can
 * never disagree about how far this actually reaches.
 */
export const FREEZE_LIFETIME_TICKS = Math.ceil((FREEZE_RANGE / FREEZE_SPEED) * TICK_RATE_HZ);

/**
 * Firings one orb is worth. **Five**, and the report left this one open — "the freeze ray should
 * maybe be unlimited but the frost should maybe be 5 uses... Maybe the freeze ray is more of a 5
 * or 10 use as well instead of timed" — so it is settled here rather than in a room.
 *
 * **Charged rather than timed, because a freeze is the one effect whose value does not decay
 * while you hold it.** A rocket window is chaos precisely because it is burning; five seconds of
 * somebody standing still is the same five seconds whenever it is spent, so a clock on it would
 * only ever mean "fire it at nobody in particular before it runs out", which is the opposite of
 * what this ability is for.
 *
 * **Five rather than ten** for the reason at the top of this file: five seconds of standing still
 * in an .io game is usually the run, so ten of them out of one orb is ten runs. It is the same
 * count the player fixed for the frost, and the two being equal is deliberate — what separates
 * them is what a use *costs to land* (a ray has to hit a moving target; a patch is spent by
 * whoever crosses it) rather than how many of them one orb is worth.
 */
export const FREEZE_CHARGES = 5;

/**
 * The gap between two rays, in ticks — **FREEZE_DURATION_TICKS, five seconds.**
 *
 * The longest cooldown in the registry, and it is derived rather than chosen: setting it to the
 * freeze's own duration buys one clean property, which is that **one orb of freeze ray can never
 * have two people frozen at the same time.** The next charge becomes available on the tick the
 * last one thaws. Anything shorter would let five charges pressed in a row put a whole cluster of
 * players on ice at once, which is a room nobody is playing in rather than an ability.
 */
export const FREEZE_FIRE_COOLDOWN_TICKS = FREEZE_DURATION_TICKS;

export const freezePowerUp: PowerUp = {
  id: POWER_UP_FREEZE,
  name: 'Freeze ray',
  description: 'Fires along your heading and freezes the first player it hits for five seconds.',
  glyph: '❄️',
  use: {
    model: 'charged',
    charges: FREEZE_CHARGES,
    cooldownTicks: FREEZE_FIRE_COOLDOWN_TICKS,
  },
  projectile: {
    motion: 'launched',
    effect: 'freeze',
    speed: FREEZE_SPEED,
    range: FREEZE_RANGE,
    lifetimeTicks: FREEZE_LIFETIME_TICKS,
    // Live from the tick it leaves, for the rocket's reason: it is launched from outside its
    // firer's own rim and cannot hit them, so there is nothing an arming delay would protect.
    armingTicks: 0,
    radius: FREEZE_RADIUS,
    standoff: PROJECTILE_MUZZLE_CLEARANCE,
    hitsOwner: false,
    freezeTicks: FREEZE_DURATION_TICKS,
  },
};
